/*eslint-disable*/
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Grid,
  Paper,
  Alert,
  Snackbar
} from "@mui/material";
import { CloudUpload, Download, Transform } from "@mui/icons-material";

const PropertyProcessorForm = ({ open, onClose, rows, onProcessed }) => {
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [processedData, setProcessedData] = useState([]);

  // Utility function to split address description into country, city, and suburb
  const parseAddressDescription = (description) => {
    // Default values
    let country = "South Africa";
    let city = "";
    let suburb = "";

    if (description) {
      // Split by commas
      const parts = description.split(",").map((part) => part.trim());

      // The last part is typically the country
      if (parts.length > 0) {
        const lastPart = parts[parts.length - 1];
        if (lastPart === "South Africa" || lastPart === "RSA") {
          country = lastPart;
          parts.pop(); // Remove country from parts
        }
      }

      // The part before country is typically city, and before that is suburb
      if (parts.length >= 2) {
        suburb = parts[0];
        city = parts[1];
      } else if (parts.length === 1) {
        suburb = parts[0];
      }
    }

    return { country, city, suburb };
  };

  // Function to convert image URL to base64 with mimeType and caption
  function splitBase64(base64String) {
    const [metadata, data] = base64String.split(",");
    const mimeType = metadata.match(/data:([^;]+);base64/)[1];
    return { mimeType, data };
  }
  function convertImageToBase64(url) {
    return fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.blob();
      })
      .then((blob) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => reject("Error reading file.");
          reader.readAsDataURL(blob);
        });
      });
  }

  // Convert a single image URL to the required object
  const convertImageToObject = async (url, caption = "", isFloorPlan = false) => {
    const base64Image = await convertImageToBase64(url);
    const { mimeType, data } = splitBase64(base64Image);
    return {
      bytes: data,
      mimeContentType: mimeType,
      caption,
      isFloorPlan
    };
  };

  // Function to process all images in a listing
  const processAllImages = async (images) => {
    const imageEntries = Object.entries(images || {});
    const processedImages = [];

    for (let i = 0; i < imageEntries.length; i++) {
      const [key, url] = imageEntries[i];
      const caption = `Image ${i + 1}`;
      // Optionally, detect floorplan by key or other logic
      const isFloorPlan = key.toLowerCase().includes("floor");
      const imageObj = await convertImageToObject(url, caption, isFloorPlan);
      processedImages.push(imageObj);
    }

    return processedImages;
  };

  // Function to normalize property type
  const normalizePropertyType = (type) => {
    if (!type) return "Unknown";

    const lowerType = type.toLowerCase();
    if (lowerType === "to let" || lowerType === "tolet") {
      return "Rental";
    } else if (lowerType === "forsale" || lowerType === "sale") {
      return "Sale";
    }

    return type; // Return original if not matched
  };

  // Main function to process a single listing
  const processListing = async (listing) => {
    try {
      // Parse address
      const addressParts = parseAddressDescription(
        listing.address?.description
      );

      // Process images
     // const processedImages = await processAllImages(listing.images || {});

      // Normalize type
      const normalizedType = normalizePropertyType(listing.type);

      // Return the processed listing
      return {
        ...listing,
        address: {
          ...listing.address,
          ...addressParts
        },
       // images: processedImages,
        type: normalizedType,
        processedAt: new Date().toISOString()
      };
    } catch (error) {
      console.error("Error processing listing:", error);
      return null;
    }
  };

  // Bulk processing function
  const bulkProcessListings = async (listings) => {
    const processedListings = [];

    for (let i = 0; i < listings.length; i++) {
      const processedListing = await processListing(listings[i]);
      if (processedListing) {
        processedListings.push(processedListing);
      }

      // Update progress
      setProgress(((i + 1) / listings.length) * 100);
    }

    return processedListings;
  };

  // Function to export data to a JSON file
  const exportToJsonFile = (data, filename) => {
    const dataStr = JSON.stringify(data, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });

    // Create download link
    const link = document.createElement("a");
    link.href = URL.createObjectURL(dataBlob);
    link.download = filename;

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleProcessData = async () => {
    if (!rows || rows.length === 0) {
      setError("No data to process");
      return;
    }

    setProcessing(true);
    setProgress(0);
    setError(null);
    setSuccess(null);

    try {
      const results = await bulkProcessListings(rows);
      setProcessedData(results);
      setSuccess(`Successfully processed ${results.length} listings`);

      // Pass processed data back to parent component
      if (onProcessed) {
        onProcessed(results);
      }
    } catch (err) {
      setError("Failed to process listings: " + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleExportData = () => {
    if (processedData.length === 0) {
      setError("No processed data to export");
      return;
    }

    try {
      exportToJsonFile(processedData, "processed_property_data.json");
      setSuccess("Data exported successfully");
    } catch (err) {
      setError("Failed to export data: " + err.message);
    }
  };

  const handleClose = () => {
    setProcessing(false);
    setProgress(0);
    setError(null);
    setSuccess(null);
    setProcessedData([]);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Transform sx={{ mr: 1 }} />
            Property Data Processor
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="textSecondary" paragraph>
            Process property listings to extract address components, convert
            images to base64, and normalize property types.
          </Typography>

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Data Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2">
                  Listings to process: <strong>{rows?.length || 0}</strong>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  Processed: <strong>{processedData.length}</strong>
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {processing && (
            <Box sx={{ width: "100%", mt: 2 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2">Processing data...</Typography>
                <Typography variant="body2">{Math.round(progress)}%</Typography>
              </Box>
              <Box sx={{ width: "100%", mt: 1 }}>
                <CircularProgress variant="determinate" value={progress} />
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleProcessData}
            disabled={processing || !rows || rows.length === 0}
            variant="outlined"
            startIcon={
              processing ? <CircularProgress size={16} /> : <Transform />
            }
          >
            Process Data
          </Button>
          <Button
            onClick={handleExportData}
            disabled={processing || processedData.length === 0}
            variant="contained"
            startIcon={<Download />}
          >
            Export Data
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PropertyProcessorForm;
